import React, { useEffect, useRef, useState } from 'react';
import { GLOBAL_DIALER_TYPES, TRUE_STRING } from '../../../helpers/CoreConstant';
import DialerHeader from './DialerHeader';
import { GlobalDialerV2Styles } from './GlobalDialerV2Styles';
import ContactList from './ContactList';
import DialerKeypad from './DialerKeypad';
import IncomingCall from './IncomingCall';
import CallConnected from './CallConnected';
import CallTransfer from './CallTransfer';
import WarmTransfer from './WarmTransfer';
import useDebounce from "../../../hooks/useDebounce";
import useDidUpdateEffect from "../../../hooks/useDidUpdateEffect";
import VirtualNumberSelector from "./VirtualNumberSelector";
import { useDispatch, useSelector } from "react-redux";
import {
    getTeamMembersStart,
    getVirtualNumbersStart, resetCallEnvironment, resetContacts,
    searchContactsStart, setCallEnvironment, setCallInfo, setGlobalDialerState, setTransferCallEnvironment
} from "../../../reducers/globalDialerV2/globalDialerSlice";
import Utils from "../../../helpers/Utils";
import { Device } from "@twilio/voice-sdk";
import {
    addParticipant,
    callCancelByCallSid,
    callCompleteByCallSid, callHangUp,
    dialerTokenRequest, endCallConference, getDialerSettings, holdCurrentCall,
    sendErrorMessageToDevelopers, unHoldCallParticipant
} from "../../../api/globalDialerApi";
import {
    callEnvironment as callEnvironmentChecking
} from "../../../api/contactApi";
import CallEnvironmentSetupAlertPop from "../../CallEnvironmentSetupAlertPop/CallEnvironmentSetupAlertPop";
import { getCurrentCredit } from "../../../actions/globalDialerAction";
import TeamList from "./TeamList";

export const GLOBAL_DIALER_CALL_STATUS = {
    DIALING: 'DIALING',
    RECEIVED: 'RECEIVED',
    INCOMING: 'INCOMING',
    HANGED_UP: 'HANGED_UP',
    RINGING: 'RINGING',
    LEFT: 'LEFT'
};

export const TRANSFER_TYPES = {
    BLIND: 'blind',
    WARM: 'warm'
};

export const CALL_RECORDING_STATUS = {
    START: 'start',
    PAUSED: 'paused',
    STOPPED: 'stopped'
};

export const GLOBAL_DIALER_SOCKET_EVENTS = {
    JOIN_CALL_ROOM: 'join-call-room',
    PARTICIPANT_JOIN: 'participant-join',
    PARTICIPANT_LEAVE: 'participant-leave',
    CALL_STARTED: 'call-started',
    CALL_RINGING: 'call-ringing',
    CALL_ANSWERED: 'call-answered',
    CALL_ENDED: 'call-ended',
    NO_ANSWER: 'no-answer'
};

export const DEVICE_EVENTS = {
    REGISTERED: 'registered',
    ERROR: 'error',
    CONNECT: 'connect',
    INCOMING: 'incoming',
    UNREGISTERED: 'unregistered'
};

export const CALL_EVENTS = {
    ACCEPT: 'accept',
    CANCEL: 'cancel',
    REJECT: 'reject',
    DISCONNECT: 'disconnect',
};

export const LOCAL_STORAGE_KEY = {
    CREATE_DEVICE_CALL_TOKEN_TRY: 'create_device_call_token_try',
    DEVICE_CALL_REGISTER: 'device-call-register',
    CALL_ENV_CHECKED: 'call-env-checked'
};

export const INOUT_STATUS = {
    IN: 'incoming',
    OUT: 'outgoing'
};

const getWithExpiry = (key) => {
    const itemStr = localStorage.getItem(key);
    if (!itemStr) {
        return null;
    }
    const item = JSON.parse(itemStr);
    const now = new Date();
    if (now.getTime() > item.expiry) {
        localStorage.removeItem(key)
        return null;
    }
    return item.value;
}

const setWithExpiry = (key, value, ttl = 3600000) => {
    const now = new Date();
    const item = {
        value: value,
        expiry: now.getTime() + ttl,
    };

    localStorage.setItem(key, JSON.stringify(item));
};

export const makeName = (item) => {
    let string = "";

    if (item.firstName) {
        string = item.firstName;
    }

    if (item.lastName) {
        if (string) {
            string += " ";
        }

        string += item.lastName;
    }

    if (!string) {
        string = item.email;
    }

    if (!string) {
        string = 'Unknown';
    }

    return string;
};

let device;
let callEvent;

const GlobalDialerV2 = ({
    openGlobalDialer,
    callTime,
    openDialog,
    closeDialog,
    dialOccurrence,
    updateNumber,
    setConnectedCalledInfo,
    popoverInnerRef2,
    OpenCallDispositionModal,
    openColdTransferred,
    closeColdTransferred,
    setTimeOfCall,
    timeOfCall
}) => {
    const userId = Utils.getAccountData('userId');
    const agencyId = Utils.getAccountData('agencyId');
    const parentId = Utils.getAccountData('parentId');
    const teamId = Utils.getAccountData('teamId');
    const centralBilling = Utils.getAccountData('centralBilling');
    const { virtualNumbers, callEnvironment, transferCallEnvironment } = useSelector((state) => (state.globalDialerV2));
    const { globalDialerSocket, currentCredit } = useSelector((state) => (state.globalDialerReducer));
    const dispatch = useDispatch();
    const classes = GlobalDialerV2Styles();
    const [dialerType, setDialerType] = useState(GLOBAL_DIALER_TYPES.DEFAULT);
    const [transferDialerType, setTransferDialerType] = useState(GLOBAL_DIALER_TYPES.DEFAULT);
    const [search, setSearch] = useState('');
    const [callingTab, setCallingTab] = useState(false);
    const [showCallEnvSetupAlertPopup, setShowCallEnvSetupAlertPopup] = useState(false);
    const [callEnvSetupAlertPopupData, setCallEnvSetupAlertPopupData] = useState(null);
    const [maximumTryToGetToken, setMaximumTryToGetToken] = useState(0);
    const [activeCallConnection, setActiveCallConnection] = useState(null);
    const [resetDialerClearTimeout, setResetDialerClearTimeout] = useState(null);
    const isCallInProgressRef = useRef(false);
    const debouncedSearch = useDebounce(search, 500);
    const [number, setNumber] = useState("");

    useEffect(() => {
        if (openGlobalDialer){
            if (!virtualNumbers[0]){
                window.buyVirtualNumberModal(true, {
                    onClose: () => {
                        openDialog();
                    }
                });
            }
        }
    }, [openGlobalDialer, virtualNumbers]);

    useEffect(() => {
        isCallInProgressRef.current = {
            callStatus: callEnvironment.isCallInProgress,
            callSid: callEnvironment.callInfo?.firstCallSid
        };

    }, [callEnvironment.isCallInProgress]);

    useEffect(() => {
        if (globalDialerSocket) {
            globalDialerSocket.emit(GLOBAL_DIALER_SOCKET_EVENTS.JOIN_CALL_ROOM, 'callRoom:' + userId);

            globalDialerSocket.on(GLOBAL_DIALER_SOCKET_EVENTS.PARTICIPANT_JOIN, (data) => {
                if (data.room === 'callRoom:' + userId) {
                    if (data.params.participant === 'participant1') {
                        dispatch(setCallEnvironment({
                            callStatus: GLOBAL_DIALER_CALL_STATUS.RECEIVED,
                            callRecordingStatus: data.params.callRecordStatus === TRUE_STRING ?
                                CALL_RECORDING_STATUS.START : CALL_RECORDING_STATUS.STOPPED
                        }));

                        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                        const time = new Date().toLocaleString();

                        setTimeOfCall({
                            time: time,
                            timezone: timeZone
                        });
                    }

                    if (data.params.participant === 'participant2') {
                        dispatch(setTransferCallEnvironment({ callStatus: GLOBAL_DIALER_CALL_STATUS.RECEIVED }));
                    }

                    if (data.params.conferenceSid) {
                        dispatch(setCallInfo({
                            conferenceSid: data.params.conferenceSid
                        }));
                    }
                }
            });

            globalDialerSocket.on(GLOBAL_DIALER_SOCKET_EVENTS.PARTICIPANT_LEAVE, (data) => {
                if (data.room === 'callRoom:' + userId) {
                    if (data.params.participant === 'participant1') {
                        dispatch(setCallEnvironment({ callStatus: GLOBAL_DIALER_CALL_STATUS.LEFT }));
                    }

                    if (data.params.participant === 'participant2') {
                        dispatch(setTransferCallEnvironment({ callStatus: GLOBAL_DIALER_CALL_STATUS.LEFT }));
                    }
                }
            });

            globalDialerSocket.on(GLOBAL_DIALER_SOCKET_EVENTS.CALL_STARTED, (data) => {
                if (data.room === 'callRoom:' + userId) {
                    let callerName = '';
                    if (!Utils.isEmpty(data.params.callerName)) {
                        callerName = data.params.callerName;
                        callerName = decodeURIComponent(callerName.replace(/\+/g, " "));
                    } else {
                        callerName = data.params.To;
                    }

                    dispatch(setCallInfo({
                        firstCallSid: data.params.callSid,
                        callerName: callerName
                    }));

                    dispatch(setGlobalDialerState({
                        lastCaller: {
                            callerName: callerName,
                            number: data.params.To,
                        }
                    }));

                    // dispatch(setCallEnvironment({
                    //     callStatus: GLOBAL_DIALER_CALL_STATUS.RECEIVED, // this was caused for immature call status
                    // }));

                    device.emit('connect', {
                        callSid: data.params.callSid,
                        token: device.token,
                        callerName: data.params.callerName ?? null,
                        From: data.params.From,
                        To: data.params.To
                    })
                }
            });

            globalDialerSocket.on(GLOBAL_DIALER_SOCKET_EVENTS.CALL_RINGING, (data) => {
                if (data.room === 'callRoom:' + userId) {
                    if (data.params.inOut === INOUT_STATUS.IN) {
                        if (data.params.contactId && Number(data.params.contactId)) {
                            dispatch(setCallEnvironment({
                                incomingCallContact: {
                                    id: data.params.contactId
                                }
                            }));
                        }

                        let callerName = '';
                        if (!Utils.isEmpty(data.params.callerName)) {
                            callerName = data.params.callerName;
                            callerName = decodeURIComponent(callerName.replace(/\+/g, " "));
                        }

                        dispatch(setCallInfo({
                            callerName: callerName
                        }));

                        dispatch(setGlobalDialerState({
                            lastCaller: {
                                callerName: callerName,
                                number: data.params.from,
                            }
                        }));
                    }
                }
            });

            globalDialerSocket.on(GLOBAL_DIALER_SOCKET_EVENTS.CALL_ANSWERED, (data) => {
                if (data.room === 'callRoom:' + userId) {
                    dispatch(setCallEnvironment({
                        callStatus: GLOBAL_DIALER_CALL_STATUS.RECEIVED,
                    }));
                }
            });

            globalDialerSocket.on(GLOBAL_DIALER_SOCKET_EVENTS.CALL_ENDED, (data) => {
                if (data.room === 'callRoom:' + userId) {
                    if ((isCallInProgressRef.current &&
                        isCallInProgressRef.current.ignoreCallEnd &&
                        isCallInProgressRef.current.callStatus) ||
                        !isCallInProgressRef.current.callStatus
                    ) {
                        if (callEnvironment.isCallInProgress) {
                            return;
                        }
                    }
                    dispatch(setCallEnvironment({
                        activeContact: {
                            contact_id: data.params.contact_id,
                            number: data.params.number,
                            first_name: data.params.first_name,
                            last_name: data.params.last_name,
                        }
                    }));

                    if (data.params.contact_id) {
                        OpenCallDispositionModal(data.params);

                        if (!timeOfCall) {
                            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                            const time = new Date().toLocaleString();

                            setTimeOfCall({
                                time: time,
                                timezone: timeZone
                            });
                        }
                    }

                    if (!callingTab) {
                        dispatch(setCallEnvironment({
                            isCallReceived: false,
                            callStatus: GLOBAL_DIALER_CALL_STATUS.HANGED_UP,
                            isCallInProgress: false,
                            incomingCallConnection: null
                        }));

                        let tempResetDialerClearTimeout = setTimeout(() => {
                            dispatch(resetCallEnvironment());
                            resetGlobalDialer();
                        }, 2000);

                        setResetDialerClearTimeout(tempResetDialerClearTimeout);
                        setActiveCallConnection(null);
                        setCallingTab(false)

                        openDialog();
                    }
                }
            });

            globalDialerSocket.on(GLOBAL_DIALER_SOCKET_EVENTS.NO_ANSWER, (data) => {
                if (data.room === 'callRoom:' + userId) {
                    if (data.room === 'callRoom:' + userId) {
                        dispatch(setCallEnvironment({
                            activeContact: {
                                contact_id: data.params.contact_id,
                                number: data.params.number,
                                first_name: data.params.first_name,
                                last_name: data.params.last_name,
                            }
                        }));

                        if (data.params.contact_id) {
                            OpenCallDispositionModal(data.params);

                            if (!timeOfCall) {
                                const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                                const time = new Date().toLocaleString();

                                setTimeOfCall({
                                    time: time,
                                    timezone: timeZone
                                });
                            }
                        }

                        dispatch(setCallEnvironment({
                            isCallReceived: false,
                            callStatus: GLOBAL_DIALER_CALL_STATUS.HANGED_UP,
                            isCallInProgress: false,
                            incomingCallConnection: null
                        }));

                        let tempResetDialerClearTimeout = setTimeout(() => {
                            dispatch(resetCallEnvironment());
                            resetGlobalDialer();
                        }, 2000)
                        setResetDialerClearTimeout(tempResetDialerClearTimeout)
                        setActiveCallConnection(null);

                        setTimeout(() => {
                            openDialog();
                        }, 500)
                    }
                }
            });
        }

        if (currentCredit == null) {
            dispatch(getCurrentCredit());
        }

        setWithExpiry(LOCAL_STORAGE_KEY.CREATE_DEVICE_CALL_TOKEN_TRY, 1);
        setupCallEnvironment();

        return () => {
            if (resetDialerClearTimeout !== null) {
                clearTimeout(resetDialerClearTimeout);
            }
        };
    }, [globalDialerSocket]);

    useEffect(() => {
        if (updateNumber.dialerContactNumber !== null && updateNumber.contactInfo) {
            const activeContact = { ...updateNumber.contactInfo };
            activeContact.contact_id = updateNumber.contactInfo.id;
            activeContact.firstName = updateNumber.contactInfo.first_name;
            activeContact.lastName = updateNumber.contactInfo.last_name;

            let tCallEnvironment = {
                activeContact: activeContact,
                dialerNumber: activeContact.number
            };

            setSearch(activeContact.number);
            getDialerSettings()
                .then((res) => {
                    let response = res.data;
                    if (response.status === 'success') {
                        if (response.data['dialer-selection'] === '2' &&
                            response.data['dialer-number'] &&
                            response.data['dialer-number'] !== ''
                        ) {
                            let fromNumber = response.data['dialer-number'].trim();
                            if (fromNumber && fromNumber !== '') {
                                if (fromNumber[0] && fromNumber[0] === '+') {
                                } else {
                                    if (fromNumber.length === 11) {
                                        fromNumber = '+' + fromNumber;
                                    } else if (fromNumber.length === 10) {
                                        fromNumber = '+1' + fromNumber;
                                    }
                                }

                                tCallEnvironment = {
                                    ...tCallEnvironment,
                                    fromNumber: fromNumber
                                };

                                dispatch(setCallEnvironment(tCallEnvironment));
                                initiateCalling(activeContact, fromNumber).then();
                            } else {
                                dispatch(setCallEnvironment(tCallEnvironment));
                            }
                        } else if (response.data['dialer-selection'] === '1') {
                            dispatch(setCallEnvironment(tCallEnvironment));
                        } else {
                            dispatch(setCallEnvironment(tCallEnvironment));
                        }
                    } else {
                        dispatch(setCallEnvironment(tCallEnvironment));
                    }
                })
                .catch((e) => {
                    dispatch(setCallEnvironment(tCallEnvironment));
                });
        } else if (updateNumber.dialerContactNumber !== null) {
            dispatch((resetCallEnvironment({
                number: updateNumber.dialerContactNumber,
                updateInitialContact: !callEnvironment.updateInitialContact
            })));
        }
    }, [updateNumber]);

    useDidUpdateEffect(() => {
        if (debouncedSearch) {
            dispatch(searchContactsStart({
                page: 1,
                size: 10,
                search: Utils.cleanPhoneNumber(debouncedSearch),
                dialer: true
            }));
        } else {
            dispatch(resetContacts());
        }
    }, [debouncedSearch]);

    useEffect(() => {
        if (activeCallConnection !== null) {
            let callerName = '';
            if (callEnvironment.incomingCallConnection !== null) {
                callerName = activeCallConnection.CallerName ? activeCallConnection.CallerName : activeCallConnection.From;
            } else if (typeof callEnvironment?.activeContact?.contact_id !== 'undefined') {
                callerName = callEnvironment.activeContact.first_name + ' ' + callEnvironment.activeContact.last_name
            } else {
                callerName = activeCallConnection.To;
            }

            try {
                setConnectedCalledInfo({
                    callerInfo: callerName,
                    endCall: async () => {
                        await rejectCall();
                    }
                });
            } catch (e) {
                try {
                    sendErrorMessageToDevelopers({
                        subject: 'call end issue',
                        body: `globalDialerV2 215 (userId : ${Utils.getAccountData('userId')}): ${e}`
                    })
                        .catch((err) => {
                            console.group("sendErrorMessageToDevelopers 256");
                            console.log(err);
                            console.groupEnd();
                        })
                } catch (e) {
                    console.log(e);
                }
            }
        } else {
            setConnectedCalledInfo(null);
        }
    }, [activeCallConnection]);

    useEffect(() => {
        dispatch(getVirtualNumbersStart());
        dispatch(getTeamMembersStart());
    }, []);

    useEffect(() => {
        if (dialOccurrence !== 0) {
            initiateCalling(null, null, callEnvironment.dialerNumber).then();
        }
    }, [dialOccurrence]);

    const resetGlobalDialer = () => {
        setSearch('');
        setDialerType(GLOBAL_DIALER_TYPES.DEFAULT);

        if (isCallInProgressRef && isCallInProgressRef.current) {
            isCallInProgressRef.current = false;
        }
    };

    const setupCallEnvironment = async () => {
        let createDeviceCallTokenTry = getWithExpiry(LOCAL_STORAGE_KEY.CREATE_DEVICE_CALL_TOKEN_TRY);

        if (createDeviceCallTokenTry !== undefined && createDeviceCallTokenTry !== null) {
            if (parseInt(createDeviceCallTokenTry) >= 4) {
                if (centralBilling && agencyId !== 236 && agencyId !== 238) {
                    try {
                        let resp = await callEnvironmentChecking();
                        if (resp && resp.success && resp.status === "error") {
                            setCallEnvSetupAlertPopupData(resp.data);
                            setShowCallEnvSetupAlertPopup(true);
                        } else {
                            setCallEnvSetupAlertPopupData(null);
                            setShowCallEnvSetupAlertPopup(false);
                        }
                    } catch (err) {
                        console.log(err);
                    } finally {
                        setWithExpiry(LOCAL_STORAGE_KEY.CALL_ENV_CHECKED, "true", 300000);
                    }
                }
                window.showNotification("ERROR", "Can not set up call environment. Please talk with our support team.");

                return;
            } else {
                setWithExpiry(LOCAL_STORAGE_KEY.CREATE_DEVICE_CALL_TOKEN_TRY, (parseInt(createDeviceCallTokenTry) + 1));
            }
        } else {
            setWithExpiry(LOCAL_STORAGE_KEY.CREATE_DEVICE_CALL_TOKEN_TRY, 1);
        }

        setMaximumTryToGetToken((prevState) => (prevState + 1));

        dialerTokenRequest(callEnvironment.fromNumber)
            .then((response) => {
                if (response?.data?.token) {
                    try {
                        device = new Device(
                            response.data.token, {
                            closeProtection: true,
                            fakeLocalDTMF: true
                        }
                        );

                        device
                            .register()
                            .catch((err) => {
                                console.group("register");
                                console.log(err);
                                console.groupEnd();
                            });
                    } catch (err) {
                        localStorage.setItem(LOCAL_STORAGE_KEY.DEVICE_CALL_REGISTER, 'false');
                        console.log('Device setup error : ' + err);
                    }

                    try {
                        device.on(DEVICE_EVENTS.REGISTERED, () => {
                            setWithExpiry(LOCAL_STORAGE_KEY.CREATE_DEVICE_CALL_TOKEN_TRY, 0);
                            setMaximumTryToGetToken(0);
                            localStorage.setItem(LOCAL_STORAGE_KEY.DEVICE_CALL_REGISTER, 'true');
                            console.log('Device is ready');
                        });

                        device.on(DEVICE_EVENTS.ERROR, (twilioError, call) => {
                            console.log('An error has occurred: ', twilioError.originalError);
                        });

                        device.on(DEVICE_EVENTS.CONNECT, (callInfo) => {
                            if (Utils.isEmpty(callInfo.callerName) || callInfo.callerName === 'null') {
                                callInfo.callerName = callInfo.To;
                            }

                            let fromNumber = callInfo.From;

                            if (fromNumber) {
                                fromNumber = "+" + fromNumber.trim();
                            }

                            let callEnvironmentData = {
                                initialContactNumber: callInfo.To,
                                fromNumber: fromNumber,
                                isCallInProgress: true,
                                // callStatus: GLOBAL_DIALER_CALL_STATUS.RECEIVED, // this was caused for immature call status
                                updateInitialContact: !callEnvironment.updateInitialContact
                            };

                            let callerName = '';
                            if (!Utils.isEmpty(callInfo.callerName)) {
                                callerName = callInfo.callerName;
                                callerName = decodeURIComponent(callerName.replace(/\+/g, " "));
                            }

                            dispatch(setCallInfo({
                                firstCallSid: callInfo.callSid,
                                token: device.token,
                                callerName: callerName,
                                callerNumber: callInfo.To
                            }));

                            dispatch(setCallEnvironment(callEnvironmentData));

                            setActiveCallConnection(callInfo);
                        });

                        device.on(DEVICE_EVENTS.INCOMING, async (call) => {
                            const isOn = await Utils.isMicrophoneOn();

                            if (isOn) {
                                console.log('Microphone is active.');
                            }

                            if (isCallInProgressRef.current.callStatus &&
                                (!isCallInProgressRef.current.callSid ||
                                    isCallInProgressRef.current.callSid !==
                                    call.parameters.CallSid
                                )) {
                                console.log('Disconnecting incoming call as another call is in progress');

                                if (call.parameters && call.parameters.CallSid) {
                                    isCallInProgressRef.current = {
                                        ...isCallInProgressRef.current,
                                        ignoreCallEnd: true
                                    };

                                    await callCancelByCallSid({
                                        callSid: call.parameters.CallSid
                                    });
                                }

                                return;
                            } else {
                                call.on(CALL_EVENTS.ACCEPT, (call) => {
                                    callEvent = call;
                                    dispatch(setCallEnvironment({
                                        callStatus: GLOBAL_DIALER_CALL_STATUS.RECEIVED
                                    }));
                                    setActiveCallConnection({
                                        ...tmpCallInfo,
                                        From: call.parameters.From
                                    });
                                });
                                call.on(CALL_EVENTS.CANCEL, async (callInfo) => {
                                    if (call && call.parameters && call.parameters.CallSid) {
                                        const res = await callCancelByCallSid({
                                            callSid: call.parameters.CallSid
                                        });

                                        if (res.data && res.data.status === true) {
                                            dispatch(setCallEnvironment({
                                                isCallReceived: false,
                                                callStatus: GLOBAL_DIALER_CALL_STATUS.HANGED_UP,
                                                isCallInProgress: false,
                                                incomingCallConnection: null
                                            }));

                                            let tempResetDialerClearTimeout = setTimeout(() => {
                                                dispatch(resetCallEnvironment());
                                                resetGlobalDialer();
                                            }, 2000);

                                            setResetDialerClearTimeout(tempResetDialerClearTimeout);
                                            closeDialog();
                                        }
                                    }
                                });
                                call.on(CALL_EVENTS.DISCONNECT, (callInfo) => {
                                    dispatch(setCallEnvironment({
                                        isCallReceived: false,
                                        callStatus: GLOBAL_DIALER_CALL_STATUS.HANGED_UP,
                                        isCallInProgress: false,
                                        incomingCallConnection: null
                                    }));

                                    let tempResetDialerClearTimeout = setTimeout(() => {
                                        dispatch(resetCallEnvironment());
                                        resetGlobalDialer();
                                    }, 2000);
                                    setResetDialerClearTimeout(tempResetDialerClearTimeout);
                                    closeDialog();
                                });
                                call.on(CALL_EVENTS.REJECT, (callInfo) => {
                                    dispatch(setCallEnvironment({
                                        isCallReceived: false,
                                        callStatus: GLOBAL_DIALER_CALL_STATUS.HANGED_UP,
                                        isCallInProgress: false,
                                        incomingCallConnection: null
                                    }));

                                    let tempResetDialerClearTimeout = setTimeout(() => {
                                        dispatch(resetCallEnvironment());
                                        resetGlobalDialer();
                                    }, 2000);
                                    setResetDialerClearTimeout(tempResetDialerClearTimeout);
                                    closeDialog();
                                });

                                let tmpCallInfo = {
                                    firstCallSid: call.parameters.CallSid,
                                    // callerName: call.customParameters.get('CallerName'),
                                    callerNumber: call.parameters.From,
                                    callerInfo: call.parameters.From
                                }

                                dispatch(setCallInfo(tmpCallInfo));
                                dispatch(setCallEnvironment({
                                    isCallReceived: true,
                                    callStatus: GLOBAL_DIALER_CALL_STATUS.INCOMING,
                                    isCallInProgress: true,
                                    goToNextInList: false,
                                    incomingCallConnection: call
                                }));
                                closeColdTransferred();
                                openDialog();
                            }
                        });

                        device.on(DEVICE_EVENTS.UNREGISTERED, () => {
                            try {
                                if (maximumTryToGetToken < 5) {
                                    setupCallEnvironment();
                                }
                            } catch (e) {
                                console.log(e);
                            }
                        })
                    } catch (err) {
                        try {
                            sendErrorMessageToDevelopers({
                                subject: 'dialer issue',
                                body: `Global Dialer V2 (userId : ${userId}) : ${err}`
                            }).catch((err) => {
                                console.group("sendErrorMessageToDevelopers");
                                console.log(err);
                                console.groupEnd();
                            });
                        } catch (e) {
                            console.log(e);
                        }
                    }
                }
            });
    };

    const initiateCalling = async (contact = null, fromNumber = null, dialNumber) => {
        const isOn = await Utils.isMicrophoneOn();

        if (!isOn) {
            return;
        }

        if (!callingTab) {
            let toNumber;
            let callerName;
            setCallingTab(true);

            if (contact) {
                toNumber = contact.number;
                callerName = makeName(contact);
            } else {
                toNumber = dialNumber;
                callerName = null;
            }

            dispatch(setCallEnvironment({
                isCallInProgress: true,
                activeContact: contact ?? {
                    number: search
                }
            }));

            dispatch(setCallInfo({
                callerName: callerName
            }));

            if (toNumber) {
                toNumber = toNumber.replace(/\D/g, '');
                if (toNumber.length > 9) {
                    if (toNumber.length === 12 && toNumber[0] !== '+') {
                        if (userId === 91 || userId === 14994) {
                            toNumber = '+' + toNumber
                        } else {
                            window.showNotification('ERROR', 'Invalid Number');
                            setCallingTab(false)
                            return;
                        }
                    } else if (toNumber.length === 12 && toNumber[0] === '+' && toNumber[1] !== '1') {
                        window.showNotification('ERROR', 'Invalid Number');
                        setCallingTab(false)
                        return;
                    } else if (toNumber.length === 13 && toNumber[0] === '+' && userId === 97) {
                        // number is ok. affan call to uae
                    } else {
                        if (toNumber.length === 11 && (userId === 91 || userId === 14994) && toNumber[0] === '9' && toNumber[1] === '7' && toNumber[2] === '1') {
                            toNumber = '+' + toNumber
                        } else if (toNumber.length === 11 && toNumber[0] !== '1') {
                            window.showNotification('ERROR', 'Invalid Number');
                            setCallingTab(false)
                            return;
                        } else if (toNumber.length === 10) {
                            toNumber = '+1' + toNumber
                        }
                    }
                } else {
                    window.showNotification('ERROR', 'Invalid Number');
                    setCallingTab(false);
                    dispatch(setCallEnvironment({ isCallInProgress: false }));
                    return;
                }
            } else {
                window.showNotification('ERROR', 'Invalid Number');
                dispatch(setCallEnvironment({ isCallInProgress: false }));
                setCallingTab(false)
                return;
            }

            let fromNumbers = fromNumber ?? callEnvironment.fromNumber;

            if (fromNumbers && fromNumbers !== '') {
                let from = fromNumbers.trim()
                if (from[0] && from[0] === '+') {
                    fromNumber = from;
                } else {
                    if (from.length === 11) {
                        fromNumber = '+' + from;
                    } else if (from.length === 10) {
                        fromNumber = '+1' + from;
                    }
                }
            }

            if (fromNumber !== '') {
                let params = {
                    To: toNumber,
                    From: fromNumber,
                    fromNumber: fromNumber,
                    TimeLimit: 7200,
                    isConferenceCall: true,
                    ContactId: contact ? contact.id : undefined,
                    // callerName: callerName,
                    dialFromMap: localStorage.getItem('isDialFromMap') === 'true',
                    powerDial: false,
                    userId: userId,
                    parentId: parentId,
                    agencyId: agencyId,
                    teamId: teamId
                };

                dispatch(setCallEnvironment({ toNumber: toNumber }));

                if (!localStorage.getItem(LOCAL_STORAGE_KEY.DEVICE_CALL_REGISTER)) {
                    try {
                        setCallingTab(false);
                        dispatch(setCallEnvironment({ isCallInProgress: false }));
                    } catch (err) {
                        console.log(err);
                    }
                } else {
                    try {
                        await device.disconnectAll();
                    } catch (e) {
                        setCallingTab(false);
                        dispatch(setCallEnvironment({ isCallInProgress: false }));
                    }
                    try {
                        callEvent = await device.connect({ params: params });
                        dispatch(setCallEnvironment({ isCallReceived: false }));
                        callEvents();
                    } catch (err) {
                        setCallingTab(false);
                        dispatch(setCallEnvironment({ isCallInProgress: false }));
                        console.log(err);
                    }
                }
            } else {
                dispatch(setCallEnvironment({ isCallInProgress: false }));
                setCallingTab(false);
                window.showNotification('SUCCESS', 'Please Reload Page and try again!');
            }
        }
    };

    const callEvents = () => {
        callEvent.on(CALL_EVENTS.CANCEL, (callInfo) => {
            dispatch(setCallEnvironment({
                isCallReceived: false,
                callStatus: GLOBAL_DIALER_CALL_STATUS.HANGED_UP,
                isCallInProgress: false,
                incomingCallConnection: null
            }));

            let tempResetDialerClearTimeout = setTimeout(() => {
                dispatch(resetCallEnvironment());
                resetGlobalDialer();
            }, 2000);

            setResetDialerClearTimeout(tempResetDialerClearTimeout);
            openDialog();
            setCallingTab(false);
        });

        callEvent.on(CALL_EVENTS.REJECT, (callInfo) => {
            dispatch(setCallEnvironment({
                isCallReceived: false,
                callStatus: GLOBAL_DIALER_CALL_STATUS.HANGED_UP,
                isCallInProgress: false,
                incomingCallConnection: null
            }));

            let tempResetDialerClearTimeout = setTimeout(() => {
                dispatch(resetCallEnvironment());
                resetGlobalDialer();
            }, 2000);

            setResetDialerClearTimeout(tempResetDialerClearTimeout);
            openDialog();
            setCallingTab(false);
        });

        callEvent.on(CALL_EVENTS.DISCONNECT, (call) => {
            dispatch(setCallEnvironment({
                isCallReceived: false,
                callStatus: GLOBAL_DIALER_CALL_STATUS.HANGED_UP,
                isCallInProgress: false
            }));

            let tempResetDialerClearTimeout = setTimeout(() => {
                dispatch(resetCallEnvironment());
                resetGlobalDialer();
            }, 2000);

            setResetDialerClearTimeout(tempResetDialerClearTimeout);
            dispatch(setCallEnvironment({
                incomingCallConnection: null
            }));
            setActiveCallConnection(null);
            setCallingTab(false)
            if (localStorage.getItem('isDialFromMap') === "true") {
                closeDialog();
            }
        });

        callEvent.on(CALL_EVENTS.CANCEL, () => {
            dispatch(setCallEnvironment({
                isCallReceived: false,
                callStatus: GLOBAL_DIALER_CALL_STATUS.HANGED_UP,
                isCallInProgress: false,
                incomingCallConnection: null
            }));

            setTimeout(() => {
                dispatch(resetCallEnvironment());
                resetGlobalDialer();
            }, 2000);

            openDialog();
            setCallingTab(false);
        });
    };

    const acceptCall = () => {
        if (callEnvironment.incomingCallConnection !== null) {
            callEnvironment.incomingCallConnection.accept();
        }
    };

    const rejectCall = async (instant = false) => {
        if (callEnvironment.isCallReceived) {
            if (callEnvironment.incomingCallConnection !== null) {
                if (callEnvironment.incomingCallConnection.parameters &&
                    callEnvironment.incomingCallConnection.parameters.CallSid
                ) {
                    try {
                        // await callCompleteByCallSid({
                        //     callSid: callEnvironment.incomingCallConnection.parameters.CallSid
                        // });

                        await callHangUp({
                            participant: 'all',
                            conferenceSid: callEnvironment.callInfo.conferenceSid,
                            isSecondCallInProgress: transferCallEnvironment.isCallInProgress,
                            callSid: callEnvironment.callInfo.firstCallSid
                        });
                    } catch (err) {
                        console.log(err);
                    }
                }
                if (!instant) {
                    dispatch(setCallEnvironment({
                        isCallReceived: false,
                        callStatus: GLOBAL_DIALER_CALL_STATUS.HANGED_UP,
                        isCallInProgress: false,
                        incomingCallConnection: null
                    }));

                    let tempResetDialerClearTimeout = setTimeout(() => {
                        dispatch(resetCallEnvironment());
                        resetGlobalDialer();
                    }, 2000)
                    setResetDialerClearTimeout(tempResetDialerClearTimeout)
                }
                device.disconnectAll();
            } else {
                try {
                    const response = await callHangUp({
                        participant: 'all',
                        conferenceSid: callEnvironment.callInfo.conferenceSid,
                        isSecondCallInProgress: transferCallEnvironment.isCallInProgress,
                        callSid: callEnvironment.callInfo.firstCallSid
                    });

                    if (response.data.status) {
                        if (!instant && callingTab) {
                            console.log('Participant removed.');
                        }
                    }
                } catch (err) {
                    console.log(err);
                }
            }
        } else {
            try {
                const response = await callHangUp({
                    participant: 'all',
                    conferenceSid: callEnvironment.callInfo.conferenceSid,
                    isSecondCallInProgress: transferCallEnvironment.isCallInProgress,
                    callSid: callEnvironment.callInfo.firstCallSid
                });

                if (response.data.status) {
                    console.log('Participant removed.');
                }
            } catch (err) {
                console.log(err);
            }
        }
    };

    const muteCall = (state) => {
        callEvent.mute(state);
    };

    const holdCall = async (state, callback) => {
        try {
            const response = await holdCurrentCall({
                isHold: state,
                participant: 'participant1',
                conferenceSid: callEnvironment.callInfo.conferenceSid
            });

            if (response?.data?.status) {
                callback(true);
            } else {
                callback(false);
                window.showNotification('error', response?.data?.message ?? 'Failed to hold the call');
            }
        } catch (err) {
            callback(false);
            window.showNotification('error', 'Failed to hold the call');
        }
    };

    const sendDigits = (digits) => {
        callEvent.sendDigits(digits);
    };

    const initiateSecondCalling = async (transferType, toNumber = null) => {

        if (!callEnvironment.callInfo?.conferenceSid) {
            window.showNotification('error', 'Unable to transfer call now. Please contact with admin.');
            return;
        }

        if (transferType === TRANSFER_TYPES.WARM && callEnvironment.callStatus !== GLOBAL_DIALER_CALL_STATUS.RECEIVED) {
            acceptCall();
        }

        dispatch(setTransferCallEnvironment({
            isCallInProgress: true,
            callStatus: GLOBAL_DIALER_CALL_STATUS.DIALING,
            transferType: transferType,
            dialerNumber: toNumber,
        }));

        const response = await addParticipant({
            toNumber: transferCallEnvironment.transferToUser ? transferCallEnvironment.transferToUser.id : (toNumber ? toNumber : transferCallEnvironment.activeContact.number),
            fromNumber: callEnvironment.fromNumber,
            firstToNumber: callEnvironment.initialContactNumber,
            transferType: transferType,
            conferenceSid: callEnvironment.callInfo.conferenceSid,
            callSid: callEnvironment.callInfo.firstCallSid,
            transferToUser: !!transferCallEnvironment.transferToUser
        });

        if (response.data.success) {
            dispatch(setCallInfo({
                secondCallSid: response.data.data.callSid
            }));

            if (transferType === TRANSFER_TYPES.BLIND) {
                dispatch(setCallEnvironment({
                    isTransferring: false
                }));

                dispatch(setTransferCallEnvironment({
                    isCallInProgress: false
                }));
                openColdTransferred();
            }

            dispatch(setCallEnvironment({
                isCallHold: true,
            }));

            dispatch(setTransferCallEnvironment({
                callStatus: GLOBAL_DIALER_CALL_STATUS.RINGING,
            }));
        }
    };

    const rejectSecondCall = async () => {
        if (transferCallEnvironment.callStatus === GLOBAL_DIALER_CALL_STATUS.LEFT ||
            transferCallEnvironment.callStatus === GLOBAL_DIALER_CALL_STATUS.RECEIVED ||
            transferCallEnvironment.callStatus === GLOBAL_DIALER_CALL_STATUS.DIALING
        ) {
            dispatch(setCallEnvironment({
                isTransferring: false,
            }));

            const response = await unHoldCallParticipant();
            if (response.data.status) {
                dispatch(setCallEnvironment({
                    isCallHold: false
                }));

                dispatch(setTransferCallEnvironment({
                    isCallHold: false,
                    isCallInProgress: false
                }));
            }
        } else {
            const response = await callHangUp({
                conferenceSid: callEnvironment.callInfo.conferenceSid,
                callSid: callEnvironment.callInfo.secondCallSid,
                firstCallSid: callEnvironment.callInfo.firstCallSid
            });

            if (response.data.status) {
                dispatch(setCallEnvironment({
                    isCallHold: false,
                    isTransferring: false
                }));
                dispatch(setTransferCallEnvironment({
                    isCallHold: false,
                    isCallInProgress: false,
                }));
            }
        }
    };

    const rejectAllCall = async () => {
        await endCallConference({
            conferenceSid: callEnvironment.callInfo.conferenceSid
        });
        dispatch(resetCallEnvironment());
        resetGlobalDialer();
    };

    const mergeCalls = async () => {
        dispatch(setCallEnvironment({
            isTransferring: false,
        }));

        const response = await unHoldCallParticipant();
        if (response.data.status) {
            dispatch(setCallEnvironment({
                isCallHold: false
            }));

            dispatch(setTransferCallEnvironment({
                isCallHold: false,
                isCallInProgress: true
            }));
        }
    };

    const onDialerTypeChange = (type, isTransferring = false) => {
        if (callEnvironment.isTransferring || isTransferring) {
            setTransferDialerType(type);
        } else {
            setDialerType(type);
        }
    };

    const renderContent = () => {
        if (callEnvironment.isCallInProgress) {
            if (callEnvironment.callStatus === GLOBAL_DIALER_CALL_STATUS.RECEIVED ||
                callEnvironment.callStatus === GLOBAL_DIALER_CALL_STATUS.DIALING) {
                return (
                    <CallConnected
                        callTime={callTime}
                        rejectCall={rejectCall}
                        muteCall={muteCall}
                        holdCall={holdCall}
                        sendDigits={sendDigits}
                        resetGlobalDialer={resetGlobalDialer}
                        onDialerTypeChange={onDialerTypeChange}
                        setNumber={setNumber}
                    />
                );
            } else if (callEnvironment.callStatus === GLOBAL_DIALER_CALL_STATUS.INCOMING) {
                return (
                    <IncomingCall
                        rejectCall={rejectCall}
                        acceptCall={acceptCall}
                    />
                );
            }
        } else if (dialerType === GLOBAL_DIALER_TYPES.KEYPAD) {
            return (
                <>
                    <DialerHeader
                        onDialerTypeChange={onDialerTypeChange}
                        onSearch={(text) => setSearch(text)}
                        dialerType={dialerType}
                    />
                    <DialerKeypad
                        initiateCalling={initiateCalling}
                        number={number}
                        setNumber={setNumber}
                        popoverInnerRef2={popoverInnerRef2}
                    />
                </>
            );
        } else if (search) {
            return (
                <>
                    <DialerHeader
                        onDialerTypeChange={onDialerTypeChange}
                        onSearch={(text) => setSearch(text)}
                        dialerType={dialerType}
                    />
                    <ContactList
                        search={search}
                        initiateCalling={initiateCalling}
                        popoverInnerRef2={popoverInnerRef2}
                    />
                </>
            );
        } else {
            return (
                <>
                    <DialerHeader
                        onDialerTypeChange={onDialerTypeChange}
                        onSearch={(text) => setSearch(text)}
                        dialerType={dialerType}
                    />
                    <VirtualNumberSelector
                        popoverInnerRef2={popoverInnerRef2}
                    />
                </>
            );
        }
    };

    const renderTransferContent = () => {
        if (callEnvironment.isTransferring) {
            if (transferDialerType === GLOBAL_DIALER_TYPES.DEFAULT) {
                return (
                    <div className='callTransferWrapperBackdrop'>
                        <div className='callTransferContainer' style={{ paddingTop: callEnvironment.isTransferring ? "25px !important" : undefined }}>
                            <DialerHeader
                                onDialerTypeChange={onDialerTypeChange}
                                onSearch={(text) => setSearch(text)}
                                dialerType={transferDialerType}
                            />
                            <CallTransfer
                                initiateSecondCalling={initiateSecondCalling}
                                search={search}
                            />
                        </div>
                    </div>
                );
            } else if (transferDialerType === GLOBAL_DIALER_TYPES.KEYPAD) {
                return (
                    <div className='callTransferWrapperBackdrop'>
                        <div className='callTransferContainer' style={{ paddingTop: callEnvironment.isTransferring ? "25px !important" : undefined }}>
                            <DialerHeader
                                onDialerTypeChange={onDialerTypeChange}
                                onSearch={(text) => setSearch(text)}
                                dialerType={transferDialerType}
                            />
                            <DialerKeypad
                                initiateCalling={initiateCalling}
                                number={number}
                                setNumber={setNumber}
                                initiateSecondCalling={initiateSecondCalling}
                                isCallTransfer
                                popoverInnerRef2={popoverInnerRef2}
                            />
                        </div>
                    </div>
                );
            } else if (transferDialerType === GLOBAL_DIALER_TYPES.TEAM_TRANSFER) {
                return (
                    <div className='callTransferWrapperBackdrop'>
                        <div className='callTransferContainer' style={{ paddingTop: callEnvironment.isTransferring ? "25px !important" : undefined }}>
                            <DialerHeader
                                onDialerTypeChange={onDialerTypeChange}
                                onSearch={(text) => setSearch(text)}
                                dialerType={transferDialerType}
                            />
                            <TeamList
                                initiateSecondCalling={initiateSecondCalling}
                            />
                        </div>
                    </div>
                );
            }
        }
    };

    return (
        <>
            <div className={classes.globalDialerV2Wrapper}>
                {
                    (transferCallEnvironment.isCallInProgress &&
                        transferCallEnvironment.transferType === TRANSFER_TYPES.WARM) ?
                        <WarmTransfer
                            setDialerType={setDialerType}
                            muteCall={muteCall}
                            sendDigits={sendDigits}
                            mergeCalls={mergeCalls}
                            rejectCall={rejectCall}
                            rejectSecondCall={rejectSecondCall}
                            rejectAllCall={rejectAllCall}
                        /> :
                        <>
                            {renderContent()}
                            {renderTransferContent()}
                        </>
                }

            </div>

            {
                showCallEnvSetupAlertPopup &&
                callEnvSetupAlertPopupData && (
                    <CallEnvironmentSetupAlertPop
                        open={showCallEnvSetupAlertPopup}
                        onClose={() => { setShowCallEnvSetupAlertPopup(false); }}
                        data={callEnvSetupAlertPopupData}
                    />
                )
            }
        </>
    );
};

export default GlobalDialerV2;